import React, { useState, useEffect } from "react";
import { FaEdit } from "react-icons/fa";
import api from "../../api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const PermissionMasterPage = () => {
  const [form, setForm] = useState({
    permission_key: "",
    title: "",
    description: ""
  });

  const [items, setItems] = useState([]);
  const [editId, setEditId] = useState(null);

  const baseURL = "/v1/admin/permissions";

  const fetchPermissions = async () => {
    try {
      const res = await api.get(`${baseURL}/list`);
      setItems(res.data?.data?.permissions ?? []);
    } catch {
      toast.error("Failed to fetch permissions ❌");
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, []);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.permission_key || !form.title)
      return toast.error("Please fill all required fields ❌");

    try {
      let res = editId
        ? await api.post(`${baseURL}/update`, { ...form, id: editId })
        : await api.post(`${baseURL}/add`, form);

      res.data?.status
        ? toast.success(editId ? "Updated successfully" : "Added successfully")
        : toast.error("Operation failed");

      setForm({ permission_key: "", title: "", description: "" });
      setEditId(null);
      fetchPermissions();
    } catch {
      toast.error("Network error ❌");
    }
  };

  const handleEdit = (item) => {
    setForm({
      permission_key: item.permission_key,
      title: item.title,
      description: item.description
    });
    setEditId(item.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.info("Edit mode enabled ✏️");
  };

  return (
    <div style={pageContainer}>
      <h2 style={mainTitle}>⚙️ Permission Master</h2>

      {/* FORM CARD */}
      <section style={cardStyle}>
        <h3 style={cardTitle}>{editId ? "✏️ Edit Permission" : "➕ Add Permission"}</h3>

        <form onSubmit={handleSubmit}>
          <div style={formGrid}>
            <FormGroup label="Permission Key">
              <input
                type="text"
                name="permission_key"
                value={form.permission_key}
                onChange={handleChange}
                style={inputStyle}
                required
              />
            </FormGroup>

            <FormGroup label="Title">
              <input
                type="text"
                name="title"
                value={form.title}
                onChange={handleChange}
                style={inputStyle}
                required
              />
            </FormGroup>

            <FormGroup label="Description" style={twoColumnFull}>
              <input
                type="text"
                name="description"
                value={form.description}
                onChange={handleChange}
                style={inputStyle}
              />
            </FormGroup>
          </div>

          <div style={{ textAlign: "right" }}>
            <button type="submit" style={submitBtn}>
              {editId ? "Update" : "Add"}
            </button>
          </div>
        </form>
      </section>

      {/* TABLE CARD */}
      <section style={cardStyle}>
        <h3 style={cardTitle}>📋 Permission List</h3>

        <div style={{ overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Key</th>
                <th style={thStyle}>Title</th>
                <th style={thStyle}>Description</th>
                <th style={thStyle}>Action</th>
              </tr>
            </thead>

            <tbody>
              {items.map((item) => (
                <tr key={item.id} style={trStyle}>
                  <td style={tdStyle}>{item.permission_key}</td>
                  <td style={tdStyle}>{item.title}</td>
                  <td style={tdStyle}>{item.description || "—"}</td>
                  <td style={tdCenter}>
                    <FaEdit
                      onClick={() => handleEdit(item)}
                      style={{ cursor: "pointer", color: "#0077cc", fontSize: 18 }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <ToastContainer position="top-right" autoClose={2500} />
    </div>
  );
};

/* ====================== FINAL POLISHED UI CSS ======================= */

const pageContainer = {
  padding: "40px 60px",
  maxWidth: "1100px",
  margin: "auto",
  background: "#f5f7fb",
  minHeight: "100vh"
};

const mainTitle = {
  fontSize: "28px",
  fontWeight: "800",
  color: "#1a1a1a",
  marginBottom: "30px",
  textAlign: "center"
};

const cardStyle = {
  background: "#ffffff",
  padding: "35px",
  borderRadius: "16px",
  boxShadow: "0 4px 15px rgba(0,0,0,0.06)",
  marginBottom: "35px",
  border: "1px solid #e7ebf3"
};

const cardTitle = {
  fontSize: "22px",
  fontWeight: "700",
  marginBottom: "22px",
  color: "#0b3d91",
  textAlign: "center"
};

const formGrid = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  columnGap: "28px",
  rowGap: "22px",
  marginTop: "25px",
};

const twoColumnFull = { gridColumn: "1 / 3" };

const inputStyle = {
  padding: "14px 16px",
  borderRadius: "10px",
  border: "1px solid #d3d9e3",
  fontSize: "15px",
  background: "#f9fbff",
  boxShadow: "0 1px 3px rgba(0,0,0,0.03)"
};

const submitBtn = {
  marginTop: "25px",
  padding: "12px 30px",
  background: "#0077cc",
  border: "none",
  borderRadius: "10px",
  color: "#fff",
  fontSize: "17px",
  fontWeight: "600",
  cursor: "pointer",
  boxShadow: "0 4px 10px rgba(0,0,0,0.15)"
};

const tableStyle = {
  width: "100%",
  borderCollapse: "separate",
  borderSpacing: "0 12px"
};

const thStyle = {
  padding: "14px",
  background: "#eef4ff",
  fontWeight: "700",
  color: "#333",
  textAlign: "center",
  fontSize: "15px"
};

const trStyle = {
  background: "#fff",
  borderRadius: "12px",
  boxShadow: "0 2px 10px rgba(0,0,0,0.05)"
};

const tdStyle = {
  padding: "14px 18px",
  fontSize: "14px",
  color: "#333"
};

const tdCenter = { ...tdStyle, textAlign: "center" };

const FormGroup = ({ label, children, style }) => (
  <div style={{ ...style, display: "flex", flexDirection: "column" }}>
    <label style={{ marginBottom: "8px", fontWeight: 600, color: "#333" }}>{label}</label>
    {children}
  </div>
);

export default PermissionMasterPage;
